import { useContext, useEffect, useState } from "react";
import Image from "next/image";
import {
  Box,
  Flex,
  SimpleGrid,
  Text,
  Tabs,
  Tab,
  TabList,
  TabPanels,
  TabPanel,
  Divider,
  Spinner,
} from "@chakra-ui/react";
import { commify } from "@ethersproject/units";
import { AiOutlineInfoCircle } from "react-icons/ai";
import { BsShieldFillCheck, BsArrowUpRight } from "react-icons/bs";
import _isEmpty from "lodash/isEmpty";

// Components
import Stake from "components/Stake";
import Unstake from "components/Unstake";
import Claim from "components/Claim";
import Tooltip from "components/Tooltip";
import DefiPreview from "components/DefiPreview";

// Contexts
import { StakeContext } from "providers/StakeProvider";

// Contants
import {
  APY_TOOLTIP_TEXT,
  AUDIT_TOOLTIP_TEXT,
  TVL_TOOLTIP_TEXT,
} from "constants/constants";
import { LEARN_MORE, TAB_CLICK, VIEW_CONTRACT } from "constants/analytics";

// Services
import { getDefiData } from "services/defi";

// Types
import { DefiData } from "types/common";
import { shortenAddress, toFixedWithoutRounding } from "utils/common";

// Hooks
import useMounted from "hooks/useMounted";
import useLedgerApp from "hooks/useLedgerApp";

const subRoute = process.env.NEXT_PUBLIC_SUB_ROUTE || "";

const MaticX = () => {
  const {
    walletInfo,
    tvl,
    conversionRate,
    loading,
    crossChainTVL,
    openAuditModal,
    config,
    track,
  } = useContext(StakeContext);

  const [defiData, setDefiData] = useState({} as DefiData);

  const isMounted = useMounted();
  const { isLedgerApp } = useLedgerApp(config);
  useEffect(() => {
    if (isMounted) {
      (async function fetchData() {
        const response: DefiData = await getDefiData();

        setDefiData(response);
      })();
    }
  }, [isMounted]);

  const { tokenSymbol, xTokenSymbol, icon } = walletInfo;

  if (loading || _isEmpty(defiData)) {
    return (
      <Flex alignItems="center" justifyContent="center" h="500px">
        <Spinner size="xl" color="brand.primary" />
      </Flex>
    );
  }

  return (
    <>
      <Box p={{ base: 0, lg: "1rem" }} pt="0">
        <Flex gap="1rem">
          <Image src={icon} width="62px" height="62px" alt="polygon-logo" />

          <Box>
            <Flex align="center" gap="1rem">
              <Text
                fontSize={{ base: "20px", md: "28px" }}
                lineHeight={{ base: "24px", md: "30px" }}
                fontFamily="medium"
              >
                Polygon
              </Text>
              <Tooltip label={AUDIT_TOOLTIP_TEXT} placement="bottom">
                <Flex
                  align="center"
                  gap="0.5rem"
                  p="0.5rem"
                  borderRadius="8px"
                  cursor="pointer"
                  color="#45BE21"
                  bg="brand.backgroundLight"
                  onClick={openAuditModal}
                >
                  <BsShieldFillCheck />
                  <Text fontSize="14px" color="brand.textColor">
                    Safe & secured
                  </Text>
                </Flex>
              </Tooltip>
            </Flex>

            <Flex
              mt="0.5rem"
              flexDir={{ base: "column", lg: "row" }}
              align={{ base: "start", lg: "center" }}
              gap="0.5rem"
              color="brand.textInactive"
            >
              <Text fontSize={{ base: "12px", md: "1rem" }}>
                Stake {tokenSymbol} and use {xTokenSymbol} while earning staking
                rewards.{" "}
                <Text
                  as="a"
                  cursor="pointer"
                  href={`${window.location.origin}${subRoute}/${window.location.search}`}
                  target={isLedgerApp ? "_blank" : "_self"}
                  fontSize={{ base: "12px", md: "1rem" }}
                  borderBottom="1px solid"
                  borderColor="brand.textInactive"
                  borderStyle="dashed"
                  color="brand.primary"
                  onClick={() => track(LEARN_MORE)}
                >
                  Learn more
                </Text>
              </Text>

              <Flex
                bg="brand.backgroundLight"
                p="4px 0.5rem"
                fontSize={{ base: 10, md: 14 }}
                borderRadius="8px"
                align="center"
                as="a"
                href={`${config.blockExplorerUrls}/token/${config.contractAddresses.xtoken.token}`}
                target="_blank"
                rel="noreferrer"
                w="fit-content"
                onClick={() => track(VIEW_CONTRACT)}
              >
                <Text lineHeight="20px">
                  Contract :{" "}
                  {shortenAddress(config.contractAddresses.xtoken.token)}{" "}
                </Text>
                <Text color="brand.primary" ml="0.5rem">
                  <BsArrowUpRight strokeWidth={0.5} />{" "}
                </Text>
              </Flex>
            </Flex>
          </Box>
        </Flex>
        <Flex
          bg="brand.backgroundLight"
          boxShadow="0px 4px 12px rgba(53, 56, 140, 0.05)"
          borderRadius="8px"
          p="1rem"
          mt="1.5rem"
          textAlign="center"
          flexDir={{ base: "column", md: "row" }}
          justifyContent="space-around"
        >
          <Flex
            flexGrow={1}
            flexDir={{ base: "row", md: "column" }}
            justifyContent="space-between"
            alignItems="center"
            gap="1rem"
            py={{ base: "0.5rem", md: "0" }}
          >
            <Flex alignItems="center">
              <Text fontSize="14px" color="brand.textInactive">
                TVL
              </Text>
              <Tooltip label={TVL_TOOLTIP_TEXT}>
                <Text ml="4px" color="brand.textInactive" fontSize="14px">
                  <AiOutlineInfoCircle />
                </Text>
              </Tooltip>
            </Flex>
            <Text fontFamily="semi-bold">
              {commify(tvl)}{" "}
              <Text as="span" fontFamily="light" fontSize="14px">
                {tokenSymbol}
              </Text>
            </Text>
          </Flex>
          <Flex
            height="32px"
            position="relative"
            top="1rem"
            display={{ base: "none", md: "block" }}
          >
            <Divider orientation="vertical" borderColor="brand.separator" />
          </Flex>
          <Flex
            flexGrow={1}
            flexDir={{ base: "row", md: "column" }}
            justifyContent="space-between"
            alignItems="center"
            gap="1rem"
            py={{ base: "0.5rem", md: "0" }}
          >
            <Flex alignItems="center">
              <Text fontSize="14px" color="brand.textInactive">
                APY
              </Text>
              <Tooltip label={APY_TOOLTIP_TEXT}>
                <Text ml="4px" color="brand.textInactive" fontSize="14px">
                  <AiOutlineInfoCircle />
                </Text>
              </Tooltip>
            </Flex>
            <Text fontFamily="semi-bold">5.76 %</Text>
          </Flex>
          <Flex
            height="32px"
            position="relative"
            top="1rem"
            display={{ base: "none", md: "block" }}
          >
            <Divider orientation="vertical" borderColor="brand.separator" />
          </Flex>
          <Flex
            flexGrow={1}
            flexDir={{ base: "row", md: "column" }}
            justifyContent="space-between"
            alignItems="center"
            gap="1rem"
            py={{ base: "0.5rem", md: "0" }}
          >
            <Flex alignItems="center">
              <Text fontSize="14px" color="brand.textInactive">
                {tokenSymbol}/{xTokenSymbol} Exchange Rates
              </Text>
            </Flex>
            <Text fontFamily="semi-bold">
              {toFixedWithoutRounding(conversionRate.toString(), 4)}{" "}
              <Text as="span" fontFamily="light" fontSize="14px">
                {tokenSymbol}
              </Text>
            </Text>
          </Flex>
        </Flex>
        <SimpleGrid columns={{ base: 1, xl: 2 }} gap="1.5rem" mt="1.5rem">
          <Box
            bg="brand.backgroundLight"
            boxShadow="0px 4px 12px rgba(53, 56, 140, 0.05)"
            borderRadius="8px"
            p="1.5rem"
          >
            <Tabs
              isFitted
              variant="unstyled"
              p="0"
              onClick={(e) =>
                track(TAB_CLICK, { tab: (e.target as any).textContent })
              }
            >
              <TabList color="brand.textInactive">
                <Tab
                  pl="0"
                  outline="none"
                  border="none"
                  _focus={{
                    outline: "none",
                  }}
                  _selected={{
                    color: "brand.textColor",
                    fontFamily: "medium",
                  }}
                >
                  <Text fontSize="20px" fontFamily="semi-bold">
                    Stake
                  </Text>
                </Tab>
                <Box height="28px" position="relative" top="12px">
                  <Divider
                    orientation="vertical"
                    borderColor="brand.separator"
                  />
                </Box>
                <Tab
                  outline="none"
                  border="none"
                  _focus={{
                    outline: "none",
                  }}
                  _selected={{
                    color: "brand.textColor",
                    fontFamily: "medium",
                  }}
                >
                  <Text fontSize="20px" fontFamily="semi-bold">
                    Unstake
                  </Text>
                </Tab>
                <Box height="28px" position="relative" top="12px">
                  <Divider
                    orientation="vertical"
                    borderColor="brand.separator"
                  />
                </Box>
                <Tab
                  pr="0"
                  outline="none"
                  border="none"
                  _focus={{
                    outline: "none",
                  }}
                  _selected={{
                    color: "brand.textColor",
                    fontFamily: "medium",
                  }}
                >
                  <Text fontSize="20px" fontFamily="semi-bold">
                    Withdraw
                  </Text>
                </Tab>
              </TabList>

              <TabPanels>
                <TabPanel mt="1rem" px="0" pb="0">
                  <Stake />
                </TabPanel>
                <TabPanel mt="1rem" px="0" pb="0">
                  <Unstake defiData={defiData} />
                </TabPanel>
                <TabPanel mt="1rem" px="0" pb="0">
                  <Claim />
                </TabPanel>
              </TabPanels>
            </Tabs>
          </Box>
          <Box>
            <DefiPreview staderTVL={crossChainTVL} defiData={defiData} />
          </Box>
        </SimpleGrid>
      </Box>
    </>
  );
};

export default MaticX;
